/**
 * Leave API service
 */

import {
  ApproveLeaveRequest,
  ApproveLeaveResponse,
  CreateLeaveRequest,
  CreateLeaveResponse,
  GetDetailedLeaveBalanceResponse,
  GetLeaveByIdResponse,
  GetLeavesRequest,
  GetLeavesResponse,
  GetPendingApprovalsRequest,
  GetPendingApprovalsResponse,
  RejectLeaveRequest,
  RejectLeaveResponse,
} from '@hrms/shared/types/leave.types';

import apiClient from './client';

export const leaveApi = {
  /**
   * Create a new leave request
   */
  async createLeave(data: CreateLeaveRequest): Promise<CreateLeaveResponse> {
    const response = await apiClient.post('/leaves', data);
    return response.data;
  },

  /**
   * Get leave requests with filters and pagination
   */
  async getLeaves(params: GetLeavesRequest): Promise<GetLeavesResponse> {
    // Remove undefined values from params
    const cleanParams = Object.fromEntries(
      Object.entries(params).filter(([_, value]) => value !== undefined)
    );
    const response = await apiClient.get('/leaves', { params: cleanParams });
    return response.data;
  },

  /**
   * Get leave request by ID
   */
  async getLeaveById(leaveId: string): Promise<GetLeaveByIdResponse> {
    const response = await apiClient.get(`/leaves/${leaveId}`);
    return response.data;
  },

  /**
   * Approve leave request
   */
  async approveLeave(leaveId: string, data: ApproveLeaveRequest): Promise<ApproveLeaveResponse> {
    const response = await apiClient.patch(`/leaves/${leaveId}/approve`, data);
    return response.data;
  },

  /**
   * Reject leave request
   */
  async rejectLeave(leaveId: string, data: RejectLeaveRequest): Promise<RejectLeaveResponse> {
    const response = await apiClient.patch(`/leaves/${leaveId}/reject`, data);
    return response.data;
  },

  /**
   * Get pending leave requests for approval
   */
  async getPendingApprovals(
    params: GetPendingApprovalsRequest
  ): Promise<GetPendingApprovalsResponse> {
    // Remove undefined values from params
    const cleanParams = Object.fromEntries(
      Object.entries(params).filter(([_, value]) => value !== undefined)
    );
    const response = await apiClient.get('/leaves/approvals', { params: cleanParams });
    return response.data;
  },

  /**
   * Get user's leave balance
   */
  async getLeaveBalance(userId: string): Promise<GetDetailedLeaveBalanceResponse> {
    const response = await apiClient.get(`/leaves/balance/${userId}`);
    return response.data;
  },

  /**
   * Get leave balance for approval context
   * Returns exact same balance data as used during leave creation
   * Ensures consistency between employee and approver views
   */
  async getApprovalLeaveBalance(leaveId: string): Promise<GetDetailedLeaveBalanceResponse> {
    const response = await apiClient.get(`/leaves/${leaveId}/approval-balance`);
    return response.data;
  },

  /**
   * Get team leave requests (for managers)
   *
   * Return type intentionally left as `any`: the real `/leaves/approvals` response
   * envelope is `{ data: { leaves, pagination } }` (see GetPendingApprovalsResponse,
   * which hits the same endpoint), but existing callers disagree on the resolved
   * shape — `useAttendanceLeave.ts` reads `response.data` as the leaves array
   * directly, while `TimesheetEntryPage.tsx` reads `leaveRes.leaves` and
   * `TimesheetApprovalsPage.tsx` reads `leaveRes.data?.leaves ?? leaveRes.leaves`.
   * Typing this accurately would break one of those call sites (two of which are
   * outside this task's scope), so it's left untyped rather than guessing which
   * caller's assumption is the "correct" one.
   */
  async getTeamLeaves(status?: string): Promise<any> {
    const params = status ? { status } : {};
    const response = await apiClient.get('/leaves/approvals', { params });
    return response.data;
  },
};
